import React from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { unitsAction } from '../../../redux/actions';
import * as notificationApi from '../../../api/notification';
import './notificationComponent.css';

// Individual notification card component
const NotificationCard = ({ data, onNotificationRead }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.token);
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high':
                return '#ff4d4f';
            case 'medium':
                return '#faad14';
            case 'low':
                return '#52c41a';
            default:
                return '#1890ff';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'warning':
                return '⚠️';
            case 'error':
                return '❌';
            case 'success':
                return '✅';
            default:
                return 'ℹ️';
        }
    };

    const markAsRead = async () => {
        if (!data?._id || data?.isRead) return;

        dispatch(unitsAction.markNotificationAsRead(data._id));
        if (onNotificationRead) onNotificationRead(data._id);

        if (!token || !data?.branchID) return;

        try {
            await notificationApi.updateNotification(data.branchID, data._id, token);
        } catch (error) {
            console.error('Update notification error', error);
        }
    };

    const resolveTargetUrl = () => {
        if (data?.branchID && data?.booking_id) {
            return `/room/${data.branchID}/${data.booking_id}`;
        }

        if (data?.branchID && data?.roomSelect && data?.checkin) {
            return `/room/${data.branchID}/${data.roomSelect}/${String(data.checkin).slice(0, 10)}`;
        }

        if (data?.branchID) {
            return `/reservation/${data.branchID}`;
        }

        return '/reservation';
    };

    const handleOpenNotification = async () => {
        await markAsRead();
        window.open(resolveTargetUrl(), '_blank');
    };

    const handleMarkAsRead = async (event) => {
        event.stopPropagation();
        await markAsRead();
    };

    return (
        <div 
            className="notification-card"
            style={{
                borderLeft: `4px solid ${getPriorityColor(data.priority)}`,
                borderRadius: '14px',
                cursor: 'pointer',
                opacity: data?.isRead ? 0.8 : 1,
            }}
            onClick={handleOpenNotification}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenNotification();
                }
            }}
        >
            <div className="notification-header">
                <span className="notification-type">
                    {getTypeIcon(data.type)}
                </span>
                <span className="notification-title">{data.title}</span>
                <span className="notification-time">
                    {format(new Date(data.createdAt), 'HH:mm dd/MM/yyyy')}
                </span>
            </div>
            <div className="notification-content">
                <p>{data.note}</p>
                {!data.isRead && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mr-5"
                        onClick={handleMarkAsRead}
                    >
                        Mark as read
                    </button>
                )}

                {data.booking_id && (
                    <small>Booking ID: {data.booking_id}</small>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                {!data.isRead ? (
                    <div className="notification-badge">New</div>
                ) : (
                    <small style={{ color: '#888' }}>Read</small>
                )}
            </div>
        </div>
    );
};

// Main component that handles the list
const NotificationCardComponent = ({ notifications, notificationViewType, setNotificationViewType, onReloadNotification, onNotificationRead, onMarkAllRead, unreadTotal = 0, totalCount = 0, limitCount = 10 }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.token);
    const unreadCount = typeof unreadTotal === 'number' ? unreadTotal : (notifications || []).filter((item) => item?.isRead === false).length;
    return (
        <div className="notification-list">
            <div className="notification-list__sticky-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', gap: '8px', foontSize: '1.2rem' }}>
                    <button
                        type="button"
                        style={{fontSize:'1.2rem'}}
                        className={`btn btn-sm ${notificationViewType === 'noRead' ? 'btn-primary' : 'btn-success'}`}
                        onClick={() => setNotificationViewType('noRead')}
                    >
                        Unread
                    </button>
                    <button
                        type="button"
                        style={{fontSize:'1.2rem'}}
                        className={`btn btn-sm ${notificationViewType === 'all' ? 'btn-primary' : 'btn-success'}`}
                        onClick={() => setNotificationViewType('all')}
                    >
                        All Notifications
                    </button>
                </div>
            </div>

            <div className="notification-list__body">
                {(!notifications || notifications.length === 0) && (
                    <div className="notification-empty">You have read all the announcements.</div>
                )}
                {notifications.map((notification) => (
                    <NotificationCard 
                        key={notification._id} 
                        data={notification}
                        onNotificationRead={onNotificationRead}
                    />
                ))}
                {unreadCount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                        <button
                            type="button"
                            style={{fontSize:'1.2rem'}}
                            className="btn btn-sm btn-danger"
                            onClick={async () => {
                                dispatch(unitsAction.markAllNotificationsAsRead());
                                if (onMarkAllRead) onMarkAllRead();

                                const branchID = notifications?.[0]?.branchID;
                                if (!token || !branchID) return;

                                try {
                                    await notificationApi.updateAllNotification(branchID, token);
                                    if (onReloadNotification) await onReloadNotification(notificationViewType || 'noRead');
                                } catch (error) {
                                    console.error('Update all notifications error', error);
                                }
                            }}
                        >
                            Mark all as read
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationCardComponent;